'use client';

import { Portal, Drawer, useBreakpoint } from 'components/pcc-components';
import { ROOT_PORTALS } from 'constants/targets';
import { useAssociatedDealer } from 'components/associated-dealers/associated-dealer-context';
import { SelectCustomerForm } from '../select-customer-form/select-customer-form';
import { DealerSearchForm } from '../dealer-search-form/dealer-search-form';
import { SearchResultsForm } from '../search-results-form/search-results-form';
import { LocationAddress } from 'services/location/types';
import { useAssociatedDealerForms } from 'components/associated-dealers/hooks/use-associated-dealer-forms';
import { DealerResponse } from 'services/dealer';
import {
  DealerAssociationResponse,
  DealerErrorResponse
} from 'services/dealer-association';
import { PAGE_BREAKPOINTS } from 'constants/common';

type AssociatedDealerDrawerContentProps = {
  isLoggedIn: boolean;
  isDealerUser: boolean;
  isInstantAccessUser: boolean;
  isCspUser: boolean;
  dealerUserResponse: DealerResponse;
  registeredUserResponse: DealerAssociationResponse | DealerErrorResponse;
  storeName: string;
  storeAddress: LocationAddress;
  isStoreLocationTandCFlag?: boolean;
  isStoreLocationListViewUpdateFlag?: boolean;
  isStoreLocationMapViewUpdateFlag?: boolean;
  isPCCAssociatedDealerFFEnabled?: boolean;
  isAssociatedDealerModalFlag?: boolean;
  isMobileDrawerActive?: boolean;
  isLoginADP?: boolean;
  showSkipThisStep?: boolean;
  isStoreLocationPinEnhancementFlag?: boolean;
  isfullMapViewMobileFlagEnabled?: boolean;
  isStoreLocationOptimizationFlag?: boolean;
  restrictedDealerStores?: string[];
};

export const AssociatedDealerDrawerContent = ({
  isDealerUser,
  isInstantAccessUser,
  isCspUser,
  isLoggedIn,
  dealerUserResponse,
  registeredUserResponse,
  storeName,
  storeAddress,
  showSkipThisStep = false,
  isStoreLocationTandCFlag = false,
  isStoreLocationListViewUpdateFlag = false,
  isPCCAssociatedDealerFFEnabled,
  isAssociatedDealerModalFlag = false,
  isMobileDrawerActive = false,
  isLoginADP = false,
  isStoreLocationMapViewUpdateFlag = false,
  isStoreLocationPinEnhancementFlag = false,
  isfullMapViewMobileFlagEnabled = false,
  isStoreLocationOptimizationFlag = false,
  restrictedDealerStores
}: AssociatedDealerDrawerContentProps) => {
  const { associatedDealerDrawer, searchResultsDrawer, selectCustomerDrawer } =
    useAssociatedDealer();
  const { dealerSearchForm, searchResultsForm, selectCustomerForm } =
    useAssociatedDealerForms();
  const isMD = useBreakpoint(PAGE_BREAKPOINTS.MD);

  const storeFlags = {
    isStoreLocationTandCFlag,
    isStoreLocationListViewUpdateFlag,
    isStoreLocationMapViewUpdateFlag,
    isPCCAssociatedDealerFFEnabled,
    isAssociatedDealerModalFlag,
    isStoreLocationPinEnhancementFlag,
    isStoreLocationOptimizationFlag,
    isfullMapViewMobileFlagEnabled
  };

  const selectCustomerFormProps = {
    showSkipThisStep,
    dealerSearchForm,
    selectCustomerForm,
    isDealerUser,
    isInstantAccessUser,
    isCspUser,
    isLoggedIn,
    dealerUserResponse,
    registeredUserResponse,
    storeName,
    storeAddress,
    showPricingWarning: true,
    storeFlags,
    isLoginADP,
    restrictedDealerStores
  };

  return (
    <Portal id={ROOT_PORTALS}>
      {isDealerUser ? (
        <>
          <Drawer
            {...associatedDealerDrawer?.drawerProps}
            isActive={
              associatedDealerDrawer?.isDrawerOpen ||
              (isMobileDrawerActive && !isMD)
            }
            hideCloseButton={isMobileDrawerActive && !isMD}
            disableBackdropClick={isMobileDrawerActive && !isMD}
            disableEscClick={isMobileDrawerActive && !isMD}
          >
            <DealerSearchForm
              dealerSearchForm={dealerSearchForm}
              dealerUserResponse={dealerUserResponse}
              isModal={false}
              isAssociatedDealerModalFlag={isAssociatedDealerModalFlag}
            />
          </Drawer>

          <Drawer
            {...searchResultsDrawer?.drawerProps}
            hideCloseButton={isMobileDrawerActive && !isMD}
            disableBackdropClick={isMobileDrawerActive && !isMD}
            disableEscClick={isMobileDrawerActive && !isMD}
          >
            <SearchResultsForm
              dealerStoreId={dealerUserResponse?.storeId}
              searchResultsForm={searchResultsForm}
              isAssociatedDealerModalFlag={isAssociatedDealerModalFlag}
            />
          </Drawer>

          <Drawer
            {...selectCustomerDrawer?.drawerProps}
            hideCloseButton={isMobileDrawerActive && !isMD}
            disableBackdropClick={isMobileDrawerActive && !isMD}
            disableEscClick={isMobileDrawerActive && !isMD}
          >
            <SelectCustomerForm {...selectCustomerFormProps} />
          </Drawer>
        </>
      ) : (
        <Drawer
          {...associatedDealerDrawer?.drawerProps}
          isActive={
            associatedDealerDrawer?.isDrawerOpen ||
            (isMobileDrawerActive && !isMD)
          }
          hideCloseButton={isMobileDrawerActive && !isMD}
          disableBackdropClick={isMobileDrawerActive && !isMD}
          disableEscClick={isMobileDrawerActive && !isMD}
        >
          <SelectCustomerForm
            {...selectCustomerFormProps}
            isMobileDrawerActive={isMobileDrawerActive}
          />
        </Drawer>
      )}
    </Portal>
  );
};
